import { AnimatePresence, motion } from 'framer-motion'
import { ChevronDown, Maximize2, Minimize2 } from 'lucide-react'
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui/button'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { ProductsPage } from 'src/pages/Products'
import { EmbeddedCatalogParamsProvider } from 'src/pages/Products/embeddedCatalogParams'

const PANEL_SIZES = {
  default: { width: 'min(90vw, 1000px)', height: '82dvh' },
  expanded: { width: 'min(98vw, 1600px)', height: '94dvh' },
} as const

const CatalogPanel = forwardRef<
  HTMLDivElement,
  { children: React.ReactNode; isExpanded: boolean }
>(({ children, isExpanded }, ref) => {
  const sizes = isExpanded ? PANEL_SIZES.expanded : PANEL_SIZES.default
  return (
    <motion.div
      ref={ref}
      initial={{
        scale: 0.2,
        opacity: 0,
        width: PANEL_SIZES.default.width,
        height: PANEL_SIZES.default.height,
      }}
      animate={{
        scale: 1,
        opacity: 1,
        width: sizes.width,
        height: sizes.height,
      }}
      exit={{ scale: 0.2, opacity: 0 }}
      transition={{
        opacity: { duration: 0.2 },
        width: { duration: 0.1, ease: 'easeInOut' },
        height: { duration: 0.1, ease: 'easeInOut' },
      }}
      style={{ transformOrigin: 'bottom right' }}
      className="fixed right-4 bottom-4 z-[60] rounded-[var(--radius-outer)] border border-[hsl(var(--border-subtle))] bg-white/95 backdrop-blur-md shadow-soft flex flex-col overflow-hidden"
    >
      <Column className="flex-1 min-h-0">{children}</Column>
    </motion.div>
  )
})
CatalogPanel.displayName = 'CatalogPanel'

export const OrderCatalogWidget = ({
  quoteId,
  open,
  onOpenChange,
}: {
  quoteId: number
  open?: boolean
  onOpenChange?: (open: boolean) => void
}) => {
  const { t } = useLocalization()
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)

  const isControlled = open != null
  const isOpen = isControlled ? open : uncontrolledOpen

  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) setUncontrolledOpen(next)
      onOpenChange?.(next)
    },
    [isControlled, onOpenChange]
  )

  const handleClose = useCallback(() => {
    setOpen(false)
    setIsExpanded(false)
  }, [setOpen])

  useEffect(() => {
    if (!isOpen) return
    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target
      if (!(target instanceof Element)) return
      if (panelRef.current?.contains(target)) return
      if (
        target.closest(
          '[data-radix-popper-content-wrapper], [role="dialog"], [role="alertdialog"], [role="listbox"], [role="menu"]'
        )
      ) {
        return
      }
      handleClose()
    }
    document.addEventListener('mousedown', handleMouseDown)
    return () => document.removeEventListener('mousedown', handleMouseDown)
  }, [isOpen, handleClose])

  return (
    <AnimatePresence>
      {isOpen && (
        <CatalogPanel ref={panelRef} isExpanded={isExpanded}>
          <Row
            className="items-center px-3 justify-between border-b border-[hsl(var(--border-subtle))] py-2 cursor-pointer gap-2"
            onClick={handleClose}
          >
            <h4 className="truncate px-2">
              {t('Browse products', 'Bläddra produkter')}
            </h4>
            <Row
              className="items-center gap-1"
              onClick={(e) => e.stopPropagation()}
            >
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation()
                  setIsExpanded((prev) => !prev)
                }}
              >
                {isExpanded ? (
                  <Minimize2 className="w-4 h-4" />
                ) : (
                  <Maximize2 className="w-4 h-4" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation()
                  handleClose()
                }}
                aria-label={t('Close', 'Stäng')}
              >
                <ChevronDown className="w-4 h-4" />
              </Button>
            </Row>
          </Row>

          <div className="flex-1 min-h-0 overflow-auto">
            <EmbeddedCatalogParamsProvider>
              <ProductsPage
                addToCurrentQuoteId={quoteId}
                showFooter={false}
                embedUseProductPreview
              />
            </EmbeddedCatalogParamsProvider>
          </div>
        </CatalogPanel>
      )}
    </AnimatePresence>
  )
}
